import { useForm } from 'react-hook-form'

import { useEffect } from 'react'

import { useEnterSubmit } from '@/hooks/ui'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

import { SwitchInput, TextInput } from '@/components/atoms'

import { AttributeValueModel } from '@/types'
import { Spinner } from '@/components/ui/spinner'

interface AttributeValueDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  createAttributeValue: Partial<AttributeValueModel.AttributeValue> | null
  editAttributeValue: AttributeValueModel.AttributeValue | null
  closeDialog: () => void
  onCreate: (data: AttributeValueFormData) => void
  onUpdate: (id: string, data: AttributeValueFormData) => void
  loading?: boolean
  isColorAttribute?: boolean
}

interface AttributeValueFormData {
  name: string
  attribute_id: string
  color_code?: string
  status: 'active' | 'inactive'
}

export const AttributeValueDialog = ({
  open,
  mode,
  createAttributeValue,
  editAttributeValue,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
  isColorAttribute = false,
}: AttributeValueDialogProps) => {
  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
    control,
  } = useForm<AttributeValueFormData>({
    defaultValues: {
      name: '',
      attribute_id: '',
      color_code: '',
      status: 'active',
    },
  })

  const formValues = watch()

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && editAttributeValue) {
      reset({
        attribute_id: editAttributeValue.attribute_id,
        name: editAttributeValue.name,
        color_code: editAttributeValue.color_code || '',
        status: editAttributeValue.status,
      })
    } else {
      reset({
        name: '',
        attribute_id: createAttributeValue?.attribute_id ?? '',
        color_code: '',
        status: 'active',
      })
    }
  }, [mode, createAttributeValue, editAttributeValue, reset])

  const onSubmit = async (data: AttributeValueFormData) => {
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editAttributeValue?._id) return
      onUpdate(editAttributeValue._id, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add value' : mode === 'edit' ? 'Edit value' : 'Value details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a new attribute value.'
              : mode === 'edit'
                ? 'Update attribute value information.'
                : 'View attribute value details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <TextInput
            control={control}
            name="name"
            label="Value"
            placeholder="e.g. Blue, Green"
            disabled={mode === 'view'}
            rules={{ required: 'Name is required' }}
            errors={errors}
          />

          {isColorAttribute && (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <TextInput
                  control={control}
                  name="color_code"
                  label="Color code (hex)"
                  placeholder="e.g. #FF0000"
                  disabled={mode === 'view'}
                  errors={errors}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-sm text-ink">Picker</Label>
                <div className="relative size-8 shrink-0">
                  <input
                    type="color"
                    className="absolute inset-0 z-10 size-full cursor-pointer opacity-0"
                    value={
                      formValues.color_code && /^#[0-9A-F]{6}$/i.test(formValues.color_code)
                        ? formValues.color_code
                        : '#000000'
                    }
                    disabled={mode === 'view'}
                    onChange={(e) => {
                      setValue('color_code', e.target.value, { shouldDirty: true })
                    }}
                  />
                  <div
                    className="size-full rounded-md border border-input transition-colors"
                    style={{ backgroundColor: formValues.color_code || '#000000' }}
                  />
                </div>
              </div>
            </div>
          )}

          <SwitchInput
            control={control}
            name="status"
            label="Status"
            description={formValues?.status === 'active' ? 'Value is visible' : 'Value is hidden'}
            disabled={mode === 'view'}
            activeValue="active"
            inactiveValue="inactive"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button type="button" disabled={loading} onClick={handleSubmit(onSubmit)}>
              {loading && <Spinner />}
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
